import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ConfirmationNumber } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { deleteVoucherCode, deleteVoucherCodeV3, getVoucherCodes } from "../../redux/actions/voucher.actions";
import { Widget } from "../user/Widget";
import useDebounce from "../../hooks/useDebounce";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { RiFileExcel2Line } from "react-icons/ri";
import { apicall } from "../../services/api";


export default function Voucher(props) {
	const { user } = useSelector((state) => state.authReducer);
	const { user_roleid } = user;
	const { batchId } = props;

	const dispatch = useDispatch();
	const [hoveredRow, setHoveredRow] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [showConfirm, setShowConfirm] = useState(false);
	const [selectedVoucherId, setSelectedVoucherId] = useState(null);
	const itemsPerPage = 10;

	const { voucherCodes, codeTotal, totalCodes, totalConsumption } = useSelector((state) => ({
		voucherCodes: state.voucherReducer?.voucherCodes,
		codeTotal: state.voucherReducer?.voucherCodes?.total || 0,
		totalCodes: state.voucherReducer?.voucherCodes?.stats?.total_codes || 0,
		totalConsumption: state.voucherReducer?.voucherCodes?.stats?.total_consumed || 0,
	}));


	const handleDownloadExcel = async () => {
		const response = await apicall(`/voucher_batch/${batchId}?is_all=true`, "get", null, {}, "v3");

		if (!response || !response.data || !response.data.data || !response.data.data.data) {
			console.error("No data available for export.");
			return;
		}

		const rows = response.data.data.data;
		if (rows.length === 0) return;

		// Optional: Format keys by replacing underscores with spaces
		const exportData = rows.map((row) => {
			const formattedRow = {};
			for (const key in row) {
				formattedRow[key.replace(/_/g, " ")] = row[key];
			}
			return formattedRow;
		});

		const worksheet = XLSX.utils.json_to_sheet(exportData);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Vouchers");

		const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
		const blob = new Blob([excelBuffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		saveAs(blob, "vouchers.xlsx");
	};



	const handleDeleteClick = (id) => {
		setSelectedVoucherId(id);
		setShowConfirm(true);
	};

	const confirmDelete = () => {
		dispatch(deleteVoucherCodeV3(selectedVoucherId));
		dispatch(getVoucherCodes(batchId, pageChange, itemsPerPage));
		setShowConfirm(false);
		setSelectedVoucherId(null);
	};

	const cancelDelete = () => {
		setShowConfirm(false);
		setSelectedVoucherId(null);
	};
	const rows = voucherCodes?.data ?? [];
	const totalPages = Math.ceil(codeTotal / itemsPerPage);

	const allowedKeys = [
		"voucher_id",
		"code",
		"consumption_type",
		"consumption_count",
		"total_count",
		"validity",
		"status"
	];

	const deleteVoucher = (id) => {
		dispatch(deleteVoucherCodeV3(id));
	};

	const onPageChange = (page) => {
		if (page >= 1 && page <= totalPages) {
			setCurrentPage(page);
		}
	};

	const pageChange = useDebounce(currentPage, 600);

	useEffect(() => {
		if (pageChange && batchId) {
			dispatch(getVoucherCodes(batchId, pageChange, itemsPerPage));
		}
	}, [pageChange, batchId]);

	return (
		<div className="w-full h-full flex flex-col items-center justify-center px-4 py-6">
			<div className="mb-6 w-full max-w-6xl">
				<Widget
					heading="TOTAL"
					icon={<ConfirmationNumber fontSize="large" color="secondary" />}
					content={`Total Codes: ${totalCodes} and Total Consumed: ${totalConsumption}`}
				/>
			</div>
			<div className="flex justify-end w-full max-w-6xl mb-2">
				<button
					onClick={handleDownloadExcel}
					className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition"
				>
					<RiFileExcel2Line />
				</button>
			</div>
			<div className="w-full max-w-6xl overflow-x-auto border border-gray-300 rounded-md shadow-md">
				<table className="min-w-full text-sm text-left border-collapse">
					<thead className="bg-blue-100 text-gray-800">
						<tr>
							{allowedKeys.map((key) => (
								<th key={key} className="p-3 border font-semibold whitespace-nowrap">
									{key.replace(/_/g, " ")}
								</th>
							))}
							{(user_roleid === 1 || user_roleid === 2 || user_roleid === 3) && (
								<th className="p-3 border text-center font-semibold">Actions</th>
							)}
						</tr>
					</thead>
					<tbody>
						{rows.length > 0 ? (
							rows.map((row, index) => (
								<tr
									key={row.voucher_id}
									onMouseEnter={() => setHoveredRow(row.voucher_id)}
									onMouseLeave={() => setHoveredRow(null)}
									className={`transition-all ${index % 2 === 0 ? "bg-white" : "bg-gray-50"
										} ${hoveredRow === row.voucher_id ? "shadow-md" : ""}`}
								>
									{allowedKeys.map((key) => (
										<td key={key} className="p-3 border whitespace-nowrap">
											{key === "status" ? (
												row[key] === true ? (
													<div className="flex items-center gap-2 text-green-600 font-semibold">
														<span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-ping"></span>
														<span className="w-2.5 h-2.5 bg-green-600 rounded-full"></span>
														<span className="ml-2">Active</span>
													</div>
												) : (
													<span className="text-red-500 font-medium">Inactive</span>
												)
											) : (
												String(row[key])
											)}
										</td>
									))}
									{(user_roleid === 1 || user_roleid === 2 || user_roleid === 3) && (
										<td className="p-3 border text-center">
											<button
												onClick={() => handleDeleteClick(row.voucher_id)}
												className="bg-red-500 text-white rounded-md p-1 hover:bg-red-600 transition"
												title="Delete"
											>
												<DeleteIcon fontSize="small" />
											</button>
										</td>
									)}
								</tr>
							))
						) : (
							<tr>
								<td
									colSpan={allowedKeys.length + 1}
									className="p-4 text-center text-gray-500"
								>
									No vouchers found.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{/* Pagination Controls */}
			{totalPages > 1 && (
				<div className="mt-4 flex flex-wrap items-center gap-2">
					{/* Previous Button */}
					<button
						disabled={currentPage === 1}
						onClick={() => onPageChange(currentPage - 1)}
						className={`px-3 py-1 border rounded ${currentPage === 1
							? "bg-gray-200 text-gray-500 cursor-not-allowed"
							: "hover:bg-gray-200"
							}`}
					>
						{"<"}
					</button>

					{[...Array(totalPages)].map((_, i) => {
						const page = i + 1;
						if (
							page === 1 ||
							page === totalPages ||
							Math.abs(currentPage - page) <= 2
						) {
							return (
								<button
									key={page}
									onClick={() => onPageChange(page)}
									className={`px-3 py-1 border rounded ${currentPage === page
										? "bg-indigo-500 text-white"
										: "hover:bg-gray-200"
										}`}
								>
									{page}
								</button>
							);
						} else if (
							(page === currentPage - 3 && page !== 1) ||
							(page === currentPage + 3 && page !== totalPages)
						) {
							return <span key={page}>...</span>;
						}
						return null;
					})}

					{/* Next Button */}
					<button
						disabled={currentPage === totalPages}
						onClick={() => onPageChange(currentPage + 1)}
						className={`px-3 py-1 border rounded ${currentPage === totalPages
							? "bg-gray-200 text-gray-500 cursor-not-allowed"
							: "hover:bg-gray-200"
							}`}
					>
						{">"}
					</button>
				</div>
			)}

			{showConfirm && (
				<div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
					<div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
						<h2 className="text-lg font-semibold text-gray-800 mb-4">
							Confirm Delete
						</h2>
						<p className="text-gray-600 mb-6">
							Are you sure you want to delete this voucher? This action cannot be undone.
						</p>
						<div className="flex justify-end gap-4">
							<button
								onClick={cancelDelete}
								className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-800"
							>
								Cancel
							</button>
							<button
								onClick={confirmDelete}
								className="px-4 py-2 rounded bg-red-500 hover:bg-red-600 text-white"
							>
								Delete
							</button>
						</div>
					</div>
				</div>
			)}

		</div>
	);
}
